<template>
  <div class="relative">
    <label v-if="label" class="block uppercase text-blueGray-600 text-xs font-bold mb-2">
      {{ label }}
    </label>

    <!-- Selected item -->
    <div
      v-if="selected"
      class="w-full flex items-center justify-between px-3 py-2 bg-blueGray-50 rounded text-sm border border-blueGray-200"
    >
      <div class="min-w-0">
        <span class="font-semibold text-blueGray-700 truncate">{{ selected.name }}</span>
        <span class="text-blueGray-400 ml-2 text-xs">{{ formatter('NGN').format(selected.price - (selected.discount || 0)) }}</span>
      </div>
      <button type="button" class="text-blueGray-400 hover:text-red-500 text-xs font-bold flex-shrink-0" @click="clear">
        Change
      </button>
    </div>

    <!-- Search input + results -->
    <div v-else>
      <input
        v-model="query"
        @input="onInput"
        type="text"
        :placeholder="placeholder"
        class="w-full px-3 py-2 bg-blueGray-50 rounded text-sm border border-blueGray-200 placeholder-blueGray-300 focus:outline-none"
      />

      <div
        v-if="query && (results.length || loaders().getItems)"
        class="absolute z-10 w-full mt-1 bg-white rounded shadow-lg border border-blueGray-100 max-h-56 overflow-y-auto"
      >
        <div v-if="loaders().getItems" class="px-3 py-3 text-xs text-blueGray-400">
          Searching...
        </div>
        <template v-else>
          <button
            v-for="item in results"
            :key="item.id"
            type="button"
            class="w-full text-left px-3 py-2 text-sm hover:bg-blueGray-50 focus:outline-none flex items-center justify-between"
            @click="choose(item)"
          >
            <span class="font-semibold text-blueGray-700 truncate">{{ item.name }}</span>
            <span class="text-blueGray-400 ml-2 text-xs flex-shrink-0">{{ formatter('NGN').format(item.price - (item.discount || 0)) }}</span>
          </button>
          <div v-if="!results.length" class="px-3 py-3 text-xs text-blueGray-400">
            No items found.
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatter } from '../../utils/formatter.util';

export default {
  name: 'ItemSearchSelect',
  props: {
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'Search products…' },
    // Full item object, not just an id — the parent needs the item's own `plans` array
    // once chosen (e.g. to populate a plan picker), and re-deriving that from a shared
    // global item list would break as soon as another search on the page changes it.
    modelValue: { type: Object, default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      query: '',
      results: [],
      debounceTimer: null,
    };
  },
  computed: {
    selected() {
      return this.modelValue;
    },
  },
  created() {
    this.formatter = formatter;
  },
  methods: {
    ...mapGetters(['loaders']),
    ...mapActions(['getItems']),

    onInput() {
      clearTimeout(this.debounceTimer);
      const q = this.query.trim();
      if (!q) {
        this.results = [];
        return;
      }
      this.debounceTimer = setTimeout(() => {
        // Deliberately NOT reading from the shared `items` getter afterwards — getItems()
        // overwrites that global list, and multiple search boxes on the same page (one
        // per order line) would otherwise stomp on each other's results. Use the
        // resolved value directly instead.
        this.getItems({ search: q })
          .then(res => {
            this.results = Array.isArray(res) ? res : (res && res.data) || [];
          })
          .catch(() => {
            this.results = [];
          });
      }, 400);
    },

    choose(item) {
      this.$emit('update:modelValue', item);
      this.results = [];
      this.query = '';
    },

    clear() {
      this.$emit('update:modelValue', null);
      this.query = '';
      this.results = [];
    },
  },
};
</script>
